import { BaseApiService } from '../base/BaseApiService';
import { ApiResponse } from '../../types/api';
import {
  CampaignKind,
  CampaignResponseDto,
  CreateCampaignDto,
  UpdateCampaignDto,
  BonificationRulesDto,
  UpdateBonificationRulesDto,
  RankingResponseDto,
  CampaignDashboardDto,
  DistributeGoalsRequestDto,
  DistributeGoalsResponseDto,
  UserGoalResponseDto,
  AdjustUserGoalDto,
  TeamGoalResponseDto,
  TeamGoalProgressDto,
  CreateTeamGoalDto,
  UpdateTeamGoalDto,
  DistributeTeamGoalsDto,
  DistributeTeamGoalsResponseDto,
  SaveManualTeamGoalsDto,
  ValidateTeamGoalAdjustmentDto,
  CampaignComparisonDto,
  TimelineMetricsDto,
  HeatmapDataDto,
  BonificationMetricsDto,
  TeamComparisonDto,
  TopPerformersDto,
  AllPerformersDto,
  CampaignSummaryDto,
  CampaignAnalyticsDashboardDto,
  PagedCampaignsResponseDto,
  MyPerformanceDto,
  TeamPerformanceDto,
  EligibleContractDto,
  PagedCampaignEligibleContractsResponseDto,
  CampaignEligibleContractLineDto,
  CampaignEligibilityRulesDto,
  UpdateCampaignEligibilityRulesDto,
  UpdateCampaignEligibilityRulesResponseDto,
  CampaignExtraResultsDto,
  PagedWeeklyBonusContractsResponseDto,
  WeeklyBonusContractLineDto,
  CampaignRevenueContractBucket,
  CampaignContractTableFilter,
  PagedContractListResponseDto,
} from '../../types/campaigns.types';
import { ContractListItemDto } from '../dashboard/dashboardService';
import {
  normalizeAllowedContractStatuses,
  normalizeCampaign,
} from '../../constants/campaignScoringDefaults';

function appendTableFilterParams(
  params: Record<string, string | number>,
  filter?: CampaignContractTableFilter
) {
  if (!filter) return;
  if (filter.cliente) params.cliente = filter.cliente;
  if (filter.cnpj) params.cnpj = filter.cnpj;
  if (filter.linhasMin != null) params.linhasMin = filter.linhasMin;
  if (filter.linhasMax != null) params.linhasMax = filter.linhasMax;
  if (filter.tipo) params.tipo = filter.tipo;
  if (filter.valorMin != null) params.valorMin = filter.valorMin;
  if (filter.valorMax != null) params.valorMax = filter.valorMax;
  if (filter.dataFrom) params.dataFrom = filter.dataFrom;
  if (filter.dataTo) params.dataTo = filter.dataTo;
  if (filter.consultor) params.consultor = filter.consultor;
  if (filter.time) params.time = filter.time;
  if (filter.gerente) params.gerente = filter.gerente;
  if (filter.status != null) params.status = filter.status;
  if (filter.pontosMin != null) params.pontosMin = filter.pontosMin;
  if (filter.pontosMax != null) params.pontosMax = filter.pontosMax;
}

function downloadCampaignExportBlob(data: BlobPart, filename: string, mimeType: string) {
  const blob = new Blob([data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

async function readBlobApiErrorMessage(data: unknown, fallback: string): Promise<string> {
  if (!(data instanceof Blob)) return fallback;
  try {
    const text = await data.text();
    if (!text) return fallback;
    try {
      const parsed = JSON.parse(text) as { message?: string };
      return parsed?.message || text;
    } catch {
      return text;
    }
  } catch {
    return fallback;
  }
}

async function throwIfBlobExportFailed(error: unknown, fallback: string): Promise<never> {
  const response = (error as { response?: { data?: unknown } })?.response;
  if (response?.data) {
    throw new Error(await readBlobApiErrorMessage(response.data, fallback));
  }
  const message = (error as { message?: string })?.message;
  throw new Error(message || fallback);
}

export class CampaignService extends BaseApiService {
  // ===== MÉTODOS BÁSICOS DE CAMPANHAS =====

  // Listar campanhas (paginado)
  async getCampaignsPaged(
    ativa?: boolean,
    kind?: CampaignKind,
    page = 1,
    pageSize = 100
  ): Promise<PagedCampaignsResponseDto> {
    const params: Record<string, unknown> = { page, pageSize };
    if (ativa !== undefined) params.ativa = ativa;
    if (kind !== undefined) params.kind = kind;
    const response = await this.api.get<ApiResponse<PagedCampaignsResponseDto>>('/v1/campaigns', { params });
    const result = this.extractData(response);
    return {
      ...result,
      items: (result.items ?? []).map(normalizeCampaign),
    };
  }

  // Listar campanhas (retorna todos os itens da primeira página com pageSize amplo)
  async getCampaigns(ativa?: boolean, kind?: CampaignKind): Promise<CampaignResponseDto[]> {
    const result = await this.getCampaignsPaged(ativa, kind, 1, 100);
    return result.items;
  }

  // Listar todas as campanhas (alias para compatibilidade)
  async getAllCampaigns(): Promise<CampaignResponseDto[]> {
    return this.getCampaigns();
  }

  // Buscar campanha por ID
  async getCampaignById(id: string): Promise<CampaignResponseDto> {
    const response = await this.api.get<ApiResponse<CampaignResponseDto>>(`/v1/campaigns/${id}`);
    return normalizeCampaign(this.extractData(response));
  }

  // Buscar campanha ativa atual
  async getCurrentCampaign(): Promise<CampaignResponseDto> {
    const response = await this.api.get<ApiResponse<CampaignResponseDto>>('/v1/campaigns/current');
    return normalizeCampaign(this.extractData(response));
  }

  // Criar campanha
  async createCampaign(data: CreateCampaignDto): Promise<CampaignResponseDto> {
    const response = await this.api.post<ApiResponse<CampaignResponseDto>>('/v1/campaigns', data);
    return normalizeCampaign(this.extractData(response));
  }

  // Atualizar campanha
  async updateCampaign(id: string, data: UpdateCampaignDto): Promise<CampaignResponseDto> {
    const response = await this.api.put<ApiResponse<CampaignResponseDto>>(`/v1/campaigns/${id}`, data);
    return normalizeCampaign(this.extractData(response));
  }

  // Ativar campanha
  async activateCampaign(id: string): Promise<void> {
    await this.api.post(`/v1/campaigns/${id}/activate`);
  }

  // Desativar campanha
  async deactivateCampaign(id: string): Promise<void> {
    await this.api.post(`/v1/campaigns/${id}/deactivate`);
  }

  // Excluir campanha (soft delete — apenas inativas)
  async deleteCampaign(id: string): Promise<void> {
    await this.api.delete(`/v1/campaigns/${id}`);
  }

  // ===== MÉTODOS DE REGRAS DE BONIFICAÇÃO =====

  // Atualizar regras de bonificação
  async updateBonificationRules(
    campaignId: string,
    data: UpdateBonificationRulesDto
  ): Promise<BonificationRulesDto> {
    const response = await this.api.put<ApiResponse<BonificationRulesDto>>(
      `/v1/campaigns/${campaignId}/bonification-rules`,
      data
    );
    return this.extractData(response);
  }

  async getEligibilityRules(campaignId: string): Promise<CampaignEligibilityRulesDto> {
    const response = await this.api.get<ApiResponse<CampaignEligibilityRulesDto>>(
      `/v1/campaigns/${campaignId}/eligibility-rules`
    );
    const rules = this.extractData(response);
    return {
      ...rules,
      allowedContractStatuses: normalizeAllowedContractStatuses(rules.allowedContractStatuses),
      meta: rules.meta
        ? {
            ...rules.meta,
            allowedContractStatuses: normalizeAllowedContractStatuses(
              rules.meta.allowedContractStatuses
            ),
            statusesEmAprovacao: normalizeAllowedContractStatuses(rules.meta.statusesEmAprovacao),
            planIds: [...(rules.meta.planIds ?? [])],
            desconsiderarPlanosSelecionados: rules.meta.desconsiderarPlanosSelecionados ?? false,
          }
        : rules.meta,
      pontos: rules.pontos
        ? {
            ...rules.pontos,
            allowedContractStatuses: normalizeAllowedContractStatuses(
              rules.pontos.allowedContractStatuses
            ),
            statusesEmAprovacao: normalizeAllowedContractStatuses(rules.pontos.statusesEmAprovacao),
            planIds: [...(rules.pontos.planIds ?? [])],
            desconsiderarPlanosSelecionados: rules.pontos.desconsiderarPlanosSelecionados ?? false,
          }
        : rules.pontos,
    };
  }

  async updateEligibilityRules(
    campaignId: string,
    data: UpdateCampaignEligibilityRulesDto
  ): Promise<UpdateCampaignEligibilityRulesResponseDto> {
    const response = await this.api.put<ApiResponse<UpdateCampaignEligibilityRulesResponseDto>>(
      `/v1/campaigns/${campaignId}/eligibility-rules`,
      data
    );
    return this.extractData(response);
  }

  async getExtraResults(campaignId: string): Promise<CampaignExtraResultsDto> {
    const response = await this.api.get<ApiResponse<CampaignExtraResultsDto>>(
      `/v1/campaigns/${campaignId}/extra-results`
    );
    return this.extractData(response);
  }

  async getWeeklyBonusContractsPaged(
    campaignId: string,
    windowIndex: number,
    userId: string,
    options?: {
      page?: number;
      pageSize?: number;
      filter?: CampaignContractTableFilter;
    }
  ): Promise<PagedWeeklyBonusContractsResponseDto> {
    const params: Record<string, string | number> = {
      page: options?.page ?? 1,
      pageSize: options?.pageSize ?? 5,
    };
    appendTableFilterParams(params, options?.filter);
    const response = await this.api.get<ApiResponse<PagedWeeklyBonusContractsResponseDto>>(
      `/v1/campaigns/${campaignId}/extra-results/weekly/${windowIndex}/consultants/${userId}/contracts`,
      { params }
    );
    return this.extractData(response);
  }

  async getWeeklyBonusContractLines(
    campaignId: string,
    windowIndex: number,
    userId: string,
    contractId: string,
  ): Promise<WeeklyBonusContractLineDto[]> {
    const response = await this.api.get<ApiResponse<WeeklyBonusContractLineDto[]>>(
      `/v1/campaigns/${campaignId}/extra-results/weekly/${windowIndex}/consultants/${userId}/contracts/${contractId}/lines`
    );
    return this.extractData(response);
  }

  async exportWeeklyBonusContracts(
    campaignId: string,
    windowIndex: number,
    userId: string,
    format: 'xlsx' | 'pdf',
    options?: { filter?: CampaignContractTableFilter },
  ): Promise<void> {
    const params: Record<string, string> = { format };
    appendTableFilterParams(params, options?.filter);
    try {
      const response = await this.api.get(
        `/v1/campaigns/${campaignId}/extra-results/weekly/${windowIndex}/consultants/${userId}/contracts/export`,
        { params, responseType: 'blob', timeout: 120000 }
      );
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const mime =
        format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      downloadCampaignExportBlob(response.data, `bonificacao-semanal-${timestamp}.${ext}`, mime);
    } catch (error) {
      await throwIfBlobExportFailed(error, 'Erro ao exportar bonificação semanal');
    }
  }

  // ===== MÉTODOS DE METAS =====

  // Distribuir metas (TeamLeader)
  async distributeGoals(
    campaignId: string,
    teamId: string
  ): Promise<DistributeGoalsResponseDto> {
    const data: DistributeGoalsRequestDto = { teamId };
    const response = await this.api.post<ApiResponse<DistributeGoalsResponseDto>>(
      `/v1/campaigns/${campaignId}/distribute-goals`,
      data
    );
    return this.extractData(response);
  }

  // Ajustar meta individual (TeamLeader)
  async adjustUserGoal(
    campaignId: string,
    userId: string,
    novaMeta: number
  ): Promise<UserGoalResponseDto> {
    const data: AdjustUserGoalDto = { novaMeta };
    const response = await this.api.put<ApiResponse<UserGoalResponseDto>>(
      `/v1/campaigns/${campaignId}/goals/${userId}`,
      data
    );
    return this.extractData(response);
  }

  // ===== MÉTODOS DE DASHBOARD E RANKING =====

  // Buscar dashboard de campanha (retorna dados específicos por role)
  async getCampaignDashboard(): Promise<CampaignDashboardDto> {
    const response = await this.api.get<ApiResponse<CampaignDashboardDto>>('/v1/campaigns/dashboard');
    return this.extractData(response);
  }

  // Buscar ranking geral (Admin)
  async getGeneralRanking(campaignId?: string): Promise<RankingResponseDto> {
    const params = campaignId ? { campaignId } : {};
    const response = await this.api.get<ApiResponse<RankingResponseDto>>('/v1/ranking/general', { params });
    return this.extractData(response);
  }

  // Buscar ranking do time (TeamLeader)
  async getTeamRanking(teamId: string, campaignId?: string): Promise<RankingResponseDto> {
    const params = campaignId ? { campaignId } : {};
    const response = await this.api.get<ApiResponse<RankingResponseDto>>(
      `/v1/ranking/team/${teamId}`,
      { params }
    );
    return this.extractData(response);
  }

  // Buscar ranking de campanha específica
  async getCampaignRanking(campaignId: string): Promise<RankingResponseDto> {
    const response = await this.api.get<ApiResponse<RankingResponseDto>>(
      `/v1/ranking/campaign/${campaignId}`
    );
    return this.extractData(response);
  }

  // ===== MÉTODOS DE TEAM GOALS =====

  // Buscar metas das equipes de uma campanha
  async getTeamGoals(campaignId: string): Promise<TeamGoalResponseDto[]> {
    const response = await this.api.get<ApiResponse<TeamGoalResponseDto[]>>(
      `/v1/campaigns/${campaignId}/team-goals`
    );
    return this.extractData(response);
  }

  // Buscar progresso de uma equipe específica
  async getTeamGoalProgress(campaignId: string, teamId: string): Promise<TeamGoalProgressDto> {
    const response = await this.api.get<ApiResponse<TeamGoalProgressDto>>(
      `/v1/campaigns/${campaignId}/team-goals/${teamId}`
    );
    return this.extractData(response);
  }

  // Distribuir metas entre equipes
  async distributeTeamGoals(
    campaignId: string,
    data: DistributeTeamGoalsDto
  ): Promise<DistributeTeamGoalsResponseDto> {
    const response = await this.api.post<ApiResponse<DistributeTeamGoalsResponseDto>>(
      `/v1/campaigns/${campaignId}/team-goals/distribute`,
      data
    );
    return this.extractData(response);
  }

  async saveManualTeamGoals(
    campaignId: string,
    data: SaveManualTeamGoalsDto
  ): Promise<DistributeTeamGoalsResponseDto> {
    const response = await this.api.post<ApiResponse<DistributeTeamGoalsResponseDto>>(
      `/v1/campaigns/${campaignId}/team-goals/manual`,
      data
    );
    return this.extractData(response);
  }

  async syncCampaignTeamSelection(
    campaignId: string,
    teamIds: string[]
  ): Promise<void> {
    await this.api.post(
      `/v1/campaigns/${campaignId}/team-goals/sync-selection`,
      { teamIds, proportionalAoTamanho: false }
    );
  }

  // Criar meta de equipe
  async createTeamGoal(campaignId: string, data: CreateTeamGoalDto): Promise<TeamGoalResponseDto> {
    const response = await this.api.post<ApiResponse<TeamGoalResponseDto>>(
      `/v1/campaigns/${campaignId}/team-goals`,
      data
    );
    return this.extractData(response);
  }

  // Atualizar meta de equipe
  async updateTeamGoal(teamGoalId: string, data: UpdateTeamGoalDto): Promise<TeamGoalResponseDto> {
    const response = await this.api.put<ApiResponse<TeamGoalResponseDto>>(
      `/v1/campaigns/team-goals/${teamGoalId}`,
      data
    );
    return this.extractData(response);
  }

  // Recalcular progresso das equipes
  async recalculateTeamProgress(campaignId: string, teamId?: string): Promise<void> {
    const params = teamId ? { teamId } : {};
    await this.api.post(`/v1/campaigns/${campaignId}/team-goals/recalculate`, null, { params });
  }

  // Validar ajuste de meta de equipe
  async validateTeamGoalAdjustment(
    campaignId: string,
    teamId: string,
    novaMeta: number
  ): Promise<ValidateTeamGoalAdjustmentDto> {
    const response = await this.api.get<ApiResponse<ValidateTeamGoalAdjustmentDto>>(
      `/v1/campaigns/${campaignId}/team-goals/${teamId}/validate`,
      { params: { novaMeta } }
    );
    return this.extractData(response);
  }

  // ===== MÉTODOS DE ANALYTICS =====

  // Comparar campanhas (retorna objeto com array de campanhas + insights)
  async compareCampaigns(campaignIds: string[]): Promise<CampaignComparisonDto> {
    const response = await this.api.post<ApiResponse<CampaignComparisonDto>>(
      '/v1/campaign-analytics/compare',
      { campaignIds }
    );
    return this.extractData(response);
  }

  // Buscar métricas de timeline
  async getTimelineMetrics(campaignId: string): Promise<TimelineMetricsDto> {
    const response = await this.api.get<ApiResponse<TimelineMetricsDto>>(
      `/v1/campaign-analytics/${campaignId}/timeline`
    );
    return this.extractData(response);
  }

  // Alias para compatibilidade
  async getCampaignTimelineMetrics(campaignId: string): Promise<TimelineMetricsDto> {
    return this.getTimelineMetrics(campaignId);
  }

  // Buscar dados de heatmap
  async getHeatmapData(campaignId: string): Promise<HeatmapDataDto> {
    const response = await this.api.get<ApiResponse<HeatmapDataDto>>(
      `/v1/campaign-analytics/${campaignId}/heatmap`
    );
    return this.extractData(response);
  }

  // Alias para compatibilidade
  async getCampaignHeatmapData(campaignId: string): Promise<HeatmapDataDto> {
    return this.getHeatmapData(campaignId);
  }

  // Buscar métricas de bonificação
  async getBonificationMetrics(campaignId: string): Promise<BonificationMetricsDto> {
    const response = await this.api.get<ApiResponse<BonificationMetricsDto>>(
      `/v1/campaign-analytics/${campaignId}/bonifications`
    );
    return this.extractData(response);
  }

  // Alias para compatibilidade
  async getCampaignBonificationMetrics(campaignId: string): Promise<BonificationMetricsDto> {
    return this.getBonificationMetrics(campaignId);
  }

  // Comparar equipes
  async compareTeams(campaignId: string): Promise<TeamComparisonDto> {
    const response = await this.api.get<ApiResponse<TeamComparisonDto>>(
      `/v1/campaign-analytics/${campaignId}/teams/compare`
    );
    return this.extractData(response);
  }

  // Alias para compatibilidade
  async getCampaignTeamComparison(campaignId: string): Promise<TeamComparisonDto> {
    return this.compareTeams(campaignId);
  }

  // Buscar top performers
  async getTopPerformers(
    campaignId: string,
    startDate?: string,
    endDate?: string
  ): Promise<TopPerformersDto> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    const response = await this.api.get<ApiResponse<TopPerformersDto>>(
      `/v1/campaign-analytics/${campaignId}/top-performers`,
      { params }
    );
    return this.extractData(response);
  }

  // Alias para compatibilidade
  async getCampaignTopPerformers(
    campaignId: string,
    startDate?: string,
    endDate?: string
  ): Promise<TopPerformersDto> {
    return this.getTopPerformers(campaignId, startDate, endDate);
  }

  // Buscar todos os performers (lista completa)
  async getAllPerformers(
    campaignId: string,
    startDate?: string,
    endDate?: string
  ): Promise<AllPerformersDto> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    const response = await this.api.get<ApiResponse<AllPerformersDto>>(
      `/v1/campaign-analytics/${campaignId}/all-performers`,
      { params }
    );
    return this.extractData(response);
  }

  // Buscar resumo da campanha (métricas principais)
  async getCampaignSummary(campaignId: string): Promise<CampaignSummaryDto> {
    const response = await this.api.get<ApiResponse<CampaignSummaryDto>>(
      `/v1/campaign-analytics/${campaignId}/summary`
    );
    return this.extractData(response);
  }

  async getCampaignAnalyticsDashboard(
    campaignId: string,
    startDate?: string,
    endDate?: string
  ): Promise<CampaignAnalyticsDashboardDto> {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await this.api.get<ApiResponse<CampaignAnalyticsDashboardDto>>(
      `/v1/campaign-analytics/${campaignId}/dashboard`,
      { params }
    );
    return this.extractData(response);
  }

  async getRevenueContracts(
    campaignId: string,
    bucket: CampaignRevenueContractBucket,
    startDate?: string,
    endDate?: string
  ): Promise<ContractListItemDto[]> {
    const params: Record<string, string> = { bucket };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await this.api.get<ApiResponse<ContractListItemDto[]>>(
      `/v1/campaign-analytics/${campaignId}/revenue-contracts`,
      { params }
    );
    return this.extractData(response);
  }

  async getRevenueContractsPaged(
    campaignId: string,
    bucket: CampaignRevenueContractBucket,
    options?: {
      startDate?: string;
      endDate?: string;
      page?: number;
      pageSize?: number;
      filter?: CampaignContractTableFilter;
    }
  ): Promise<PagedContractListResponseDto> {
    const params: Record<string, string | number> = {
      bucket,
      page: options?.page ?? 1,
      pageSize: options?.pageSize ?? 5,
    };
    if (options?.startDate) params.startDate = options.startDate;
    if (options?.endDate) params.endDate = options.endDate;
    appendTableFilterParams(params, options?.filter);
    const response = await this.api.get<ApiResponse<PagedContractListResponseDto>>(
      `/v1/campaign-analytics/${campaignId}/revenue-contracts`,
      { params }
    );
    return this.extractData(response);
  }

  async getPointsContractsPaged(
    campaignId: string,
    options?: {
      startDate?: string;
      endDate?: string;
      page?: number;
      pageSize?: number;
      filter?: CampaignContractTableFilter;
    }
  ): Promise<PagedContractListResponseDto> {
    const params: Record<string, string | number> = {
      page: options?.page ?? 1,
      pageSize: options?.pageSize ?? 5,
    };
    if (options?.startDate) params.startDate = options.startDate;
    if (options?.endDate) params.endDate = options.endDate;
    appendTableFilterParams(params, options?.filter);
    const response = await this.api.get<ApiResponse<PagedContractListResponseDto>>(
      `/v1/campaign-analytics/${campaignId}/points-contracts`,
      { params }
    );
    return this.extractData(response);
  }

  async exportRevenueContracts(
    campaignId: string,
    bucket: CampaignRevenueContractBucket,
    format: 'xlsx' | 'pdf',
    options?: {
      startDate?: string;
      endDate?: string;
      filter?: CampaignContractTableFilter;
    }
  ): Promise<void> {
    const params: Record<string, string> = { bucket, format };
    if (options?.startDate) params.startDate = options.startDate;
    if (options?.endDate) params.endDate = options.endDate;
    appendTableFilterParams(params, options?.filter);
    try {
      const response = await this.api.get(
        `/v1/campaign-analytics/${campaignId}/revenue-contracts/export`,
        { params, responseType: 'blob', timeout: 120000 }
      );
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const mime =
        format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      downloadCampaignExportBlob(response.data, `contratos-campanha-${timestamp}.${ext}`, mime);
    } catch (error) {
      await throwIfBlobExportFailed(error, 'Erro ao exportar contratos da campanha');
    }
  }

  async exportPointsContracts(
    campaignId: string,
    format: 'xlsx' | 'pdf',
    options?: {
      startDate?: string;
      endDate?: string;
    }
  ): Promise<void> {
    const params: Record<string, string> = { format };
    if (options?.startDate) params.startDate = options.startDate;
    if (options?.endDate) params.endDate = options.endDate;
    try {
      const response = await this.api.get(
        `/v1/campaign-analytics/${campaignId}/points-contracts/export`,
        { params, responseType: 'blob', timeout: 120000 }
      );
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const mime =
        format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      downloadCampaignExportBlob(response.data, `pontos-campanha-${timestamp}.${ext}`, mime);
    } catch (error) {
      await throwIfBlobExportFailed(error, 'Erro ao exportar contratos de pontuação');
    }
  }

  async exportPerformers(
    campaignId: string,
    format: 'xlsx' | 'pdf',
    startDate?: string,
    endDate?: string
  ): Promise<void> {
    const params: Record<string, string> = { format };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    try {
      const response = await this.api.get(
        `/v1/campaign-analytics/${campaignId}/performers/export`,
        { params, responseType: 'blob', timeout: 120000 }
      );
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const mime =
        format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      downloadCampaignExportBlob(response.data, `ranking-campanha-${timestamp}.${ext}`, mime);
    } catch (error) {
      await throwIfBlobExportFailed(error, 'Erro ao exportar ranking da campanha');
    }
  }

  // ===== PERFORMANCE (Fase 2) =====

  async getMyPerformance(campaignId?: string): Promise<MyPerformanceDto> {
    const params = campaignId ? { campaignId } : {};
    const response = await this.api.get<ApiResponse<MyPerformanceDto>>('/v1/campaigns/performance/me', { params });
    return this.extractData(response);
  }

  async getTeamPerformance(campaignId?: string): Promise<TeamPerformanceDto> {
    const params = campaignId ? { campaignId } : {};
    const response = await this.api.get<ApiResponse<TeamPerformanceDto>>('/v1/campaigns/performance/team', { params });
    return this.extractData(response);
  }

  async getPerformanceCampaigns(): Promise<CampaignResponseDto[]> {
    const response = await this.api.get<ApiResponse<CampaignResponseDto[]>>(
      '/v1/campaigns/performance/campaigns'
    );
    return this.extractData(response);
  }

  async getEligibleContracts(
    campaignId: string,
    options?: {
      userId?: string;
      teamId?: string;
      page?: number;
      pageSize?: number;
      bucket?: 'Aprovada' | 'EmAprovacao';
      filter?: CampaignContractTableFilter;
    }
  ): Promise<PagedCampaignEligibleContractsResponseDto> {
    const params: Record<string, string | number> = {};
    if (options?.userId) params.userId = options.userId;
    if (options?.teamId) params.teamId = options.teamId;
    if (options?.page) params.page = options.page;
    if (options?.pageSize) params.pageSize = options.pageSize;
    if (options?.bucket) params.bucket = options.bucket;
    appendTableFilterParams(params, options?.filter);
    const response = await this.api.get<ApiResponse<PagedCampaignEligibleContractsResponseDto>>(
      `/v1/campaigns/performance/${campaignId}/eligible-contracts`,
      { params }
    );
    return this.extractData(response);
  }

  async exportEligibleContracts(
    campaignId: string,
    format: 'xlsx' | 'pdf',
    options?: {
      userId?: string;
      teamId?: string;
      bucket?: 'Aprovada' | 'EmAprovacao';
      filter?: CampaignContractTableFilter;
    }
  ): Promise<void> {
    const params: Record<string, string> = { format };
    if (options?.userId) params.userId = options.userId;
    if (options?.teamId) params.teamId = options.teamId;
    if (options?.bucket) params.bucket = options.bucket;
    appendTableFilterParams(params, options?.filter);
    try {
      const response = await this.api.get(
        `/v1/campaigns/performance/${campaignId}/eligible-contracts/export`,
        { params, responseType: 'blob', timeout: 120000 }
      );
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const mime =
        format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const bucketSlug = options?.bucket === 'EmAprovacao' ? 'em-aprovacao' : 'elegiveis';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      downloadCampaignExportBlob(response.data, `contratos-${bucketSlug}-${timestamp}.${ext}`, mime);
    } catch (error) {
      await throwIfBlobExportFailed(error, 'Erro ao exportar contratos elegíveis');
    }
  }

  async getEligibleContractLines(
    campaignId: string,
    contractId: string,
    options?: {
      userId?: string;
      teamId?: string;
      bucket?: 'Aprovada' | 'EmAprovacao';
    }
  ): Promise<CampaignEligibleContractLineDto[]> {
    const params: Record<string, string> = {};
    if (options?.userId) params.userId = options.userId;
    if (options?.teamId) params.teamId = options.teamId;
    if (options?.bucket) params.bucket = options.bucket;
    const response = await this.api.get<ApiResponse<CampaignEligibleContractLineDto[]>>(
      `/v1/campaigns/performance/${campaignId}/eligible-contracts/${contractId}/lines`,
      { params }
    );
    return this.extractData(response);
  }

  // ===== MÉTODOS DE RECÁLCULO =====

  // Recalcular todas as métricas de uma campanha (UserGoals + TeamGoals)
  async recalculateAllMetrics(campaignId: string): Promise<{ message: string; recalculando?: boolean }> {
    const response = await this.api.post<ApiResponse<{ message: string; recalculando?: boolean }>>(
      `/v1/campaigns/${campaignId}/recalculate-all`
    );
    return this.extractData(response);
  }

  /** Aguarda o job de recálculo em background concluir (flag recalculando). */
  async waitForRecalculationComplete(
    campaignId: string,
    options?: { intervalMs?: number; maxWaitMs?: number }
  ): Promise<void> {
    const intervalMs = options?.intervalMs ?? 2000;
    const maxWaitMs = options?.maxWaitMs ?? 10 * 60 * 1000;
    const started = Date.now();

    while (Date.now() - started < maxWaitMs) {
      const rules = await this.getEligibilityRules(campaignId);
      if (!rules.recalculando) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    throw new Error(
      'O recálculo da campanha está demorando mais que o esperado. Tente atualizar novamente em instantes.'
    );
  }
}

// Instância singleton
export const campaignService = new CampaignService();
export default campaignService;

